import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { Box } from '@cad-arc';
import {
    ESRI_EXTENT_RECOVERY_RETRY_LIMIT,
    ESRI_MAP_DEFAULT_ROTATION_ENABLED,
} from '../configurations';
import { Extent, MapView, Point, WebMap, webMercatorUtils, Color } from '../constructors';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint, MarginBounds } from '../../location/types';
import type { EsriMapInterface, MapOnChangeParams } from '../types';
import { logEsriError } from '../helpers/utils';
import { createInitialWebMap } from './helpers';
import { MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY } from '../configurations';

// eslint-disable-next-line no-restricted-imports
import '@arcgis/core/assets/esri/themes/dark/main.css';
import { colors } from '~/colors/colors';

/**
 * Esri Map used in map window
 */
type UseEsriESModuleMapProps = {
    zoom: number;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
    mapOnChange: (data: MapOnChangeParams) => void;
    mapOnUnmount?: () => void;
    center?: GeoPoint;
    isAlternativeBasemap?: boolean;
    webmapPortalId?: string;
    shouldResetMapOnInitialLoad?: boolean;
};
const useEsriESModuleMap = ({
    zoom,
    mapRef,
    mapOnChange,
    mapOnUnmount,
    center,
    isAlternativeBasemap,
    webmapPortalId,
    shouldResetMapOnInitialLoad,
}: UseEsriESModuleMapProps): EsriMapInterface => {
    const [webMap, setWebMap] = useState<WebMap>();
    const [mapView, setMapView] = useState<MapView>();
    const [isWebGL2Supported, setIsWebGL2Supported] = useState<boolean>(true);
    const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
    const [isMapLoadingFailed, setIsMapLoadingFailed] = useState<boolean>(false);
    const prevCenterRef = useRef<GeoPoint>();
    const prevZoomRef = useRef<number>();
    const extentRef = useRef<Extent | undefined>();
    const readyRef = useRef(false);
    const retriesRef = useRef(0);
    const initialZoom = useRef<number>(zoom);

    const setMapLoadFailed = useCallback((failed: boolean) => {
        if (failed) {
            storageHelpers.setItem(
                MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                new Date().toISOString()
            );
        }
        setIsMapLoadingFailed(failed);
    }, []);

    useEffect(() => {
        if (!center) {
            return;
        }
        prevCenterRef.current = { latitude: center!.latitude, longitude: center!.longitude };

        const point = new Point(center);

        if (!mapView || point.equals(mapView.center)) {
            return;
        }

        mapView
            .goTo({
                center: [center.longitude, center.latitude],
            })
            .catch(() => {});
    }, [center, mapView]);

    useEffect(() => {
        if (!!mapView || !center || !isValidLatLng(center?.latitude, center?.longitude)) {
            return;
        }

        const webmap = createInitialWebMap({
            webmapPortalId,
            isAlternativeBasemap,
            setMapLoadFailed,
        });

        // Create View
        const view = new MapView({
            container: mapRef.current || undefined,
            map: webmap,
            center: center as Point,
            zoom: initialZoom.current,
            constraints: {
                rotationEnabled: ESRI_MAP_DEFAULT_ROTATION_ENABLED,
            },
            highlightOptions: {
                color: new Color(colors.esriColors.highlightColor),
                fillOpacity: 0.16,
                haloOpacity: 1,
            },
        });

        view.when(() => {
            if (window.Cypress || process.env.NODE_ENV === 'development') {
                window.esriMapView = view;
                window.esriMapRef = mapRef;

                const canvases = mapRef.current?.querySelectorAll('canvas');
                if (canvases && canvases.length > 0) {
                    const canvas = canvases[0];
                    window.esriMapViewCanvas = canvas;
                }
            }

            // Setup fatal error watch inside when() to guarantee readiness.
            reactiveUtils.when(
                () => view.fatalError,
                (fatalError) => {
                    logEsriError({
                        category: 'fatal_error',
                        extraData: { error: fatalError },
                    });

                    view.tryFatalErrorRecovery();
                }
            );
        }).catch((error) => {
            setMapLoadFailed(true);
            if (error.name?.includes('webgl')) {
                setIsWebGL2Supported(false);
                logEsriError({
                    category: 'webgl_not_supported',
                    error,
                });
            } else {
                logEsriError({
                    category: 'map_view_error',
                    error,
                });
            }
        });

        // Set General popup rules
        view.popup.dockOptions = {
            position: 'bottom-center',
        };

        // Initialize prevCenter and prevZoom values
        prevCenterRef.current = center;
        prevZoomRef.current = view.zoom;

        const resetExtent = async () => {
            if (!prevCenterRef.current) {
                return;
            }

            const center = { ...prevCenterRef.current };

            try {
                await view.goTo({
                    center: [center.longitude + 0.000001, center.latitude],
                    zoom: prevZoomRef.current,
                });

                await view.goTo({
                    center: [center.longitude, center.latitude],
                    zoom: prevZoomRef.current,
                });
            } catch (e) {
                // Swallow error.
            }
        };

        const getMarginBounds = (): MarginBounds | undefined => {
            if (!view.extent) {
                return;
            }

            const northWestLngLat = webMercatorUtils.xyToLngLat(view.extent.xmin, view.extent.ymax);
            const southEastLngLat = webMercatorUtils.xyToLngLat(view.extent.xmax, view.extent.ymin);

            return {
                nw: { latitude: northWestLngLat[1], longitude: northWestLngLat[0] },
                se: { latitude: southEastLngLat[1], longitude: southEastLngLat[0] },
            };
        };

        const resetMap = () => {
            if (!center) {
                return;
            }
            view.goTo({
                center: [center!.longitude, center!.latitude],
                zoom,
            }).catch(() => {});
        };

        /**
         * This function serves as a fallback for when the map doesn't have an extent (bbox). We've had
         * Sentry errors where that happens and that breaks the code. So what we do here is to move the
         * map by only a few meters/feet and then move it back to its original coordinates so that this
         * forces the map to obtain an extent. If that fails 10 times, then we reset the map entirely.
         */
        const failedExtentRecovery = () => {
            if (retriesRef.current < ESRI_EXTENT_RECOVERY_RETRY_LIMIT) {
                retriesRef.current++;
                resetExtent();
            } else {
                retriesRef.current = 0;
                resetMap();
            }
        };

        const applyChanges = (extent?: Extent, zoom?: number) => {
            const currentCenter = {
                latitude: (extent || view).center.latitude,
                longitude: (extent || view).center.longitude,
            };
            const marginBounds = getMarginBounds();

            if (!marginBounds) {
                failedExtentRecovery();

                return;
            }

            retriesRef.current = 0;

            mapOnChange({
                center: currentCenter,
                zoom: zoom ? zoom : view.zoom,
                marginBounds,
            });
            prevCenterRef.current = currentCenter;
        };

        view.watch(
            'zoom',
            debounce((newZoom: number, oldZoom: number) => {
                if (Math.round(newZoom) === Math.round(oldZoom)) {
                    return;
                }

                applyChanges(undefined, newZoom);
                prevZoomRef.current = newZoom;
            }, 50)
        );

        view.watch('ready', (isReady) => {
            if (!isReady || readyRef.current) {
                return;
            }

            applyChanges();
            prevZoomRef.current = view.zoom;
            extentRef.current = view.extent;
            readyRef.current = true;

            if (!view.extent) {
                view.goTo(
                    extentRef.current || {
                        center: [center?.longitude, center?.latitude],
                    }
                ).catch(() => {});
            }
            setIsMapLoading(false);
            if (shouldResetMapOnInitialLoad) {
                // when map opens for the fist time at default center, reset map to department zoom level. (we have a default zoom level that is more closer for viewing entities on map. Only for the first map load, we set map to department zoom level)
                // resetMap will reset map center and zoom level. rest center is noop here since map is already on default center. calling resetMap just for department zoom level.
                resetMap();
            }
        });

        view.watch(
            'extent',
            debounce((newExtent: Extent | undefined) => {
                if (!newExtent) {
                    // TODO: Determine if we will need something like this in the off chance that the
                    // new extent is null again.
                    if (prevCenterRef.current) {
                        failedExtentRecovery();
                    }

                    return;
                }

                retriesRef.current = 0;

                if (!extentRef.current || !newExtent.equals(extentRef.current)) {
                    applyChanges(newExtent);
                    extentRef.current = newExtent;
                    prevZoomRef.current = view.zoom;
                }
            }, 50)
        );

        view.map.allLayers.on('change', (event) => {
            // change event fires after an item has been added, moved or removed from the collection.
            // event.added returns an array of added layers
            if (event.added.length > 0) {
                // reorder server side layers so they show up beneath client side ones
                const serverSideLayers = event.added
                    // @ts-expect-error Property 'portalItem' does not exist on type 'Layer'.ts(2339)
                    .filter((layer) => !!layer.portalItem)
                    .reverse();

                serverSideLayers.forEach((layer) => webmap.reorder(layer, 0));
            }
        });

        view.on('layerview-create-error', (event) => {
            logEsriError({
                category: 'layerview_create_error',
                extraData: {
                    layerId: event?.layer?.id,
                    layerTitle: event?.layer?.title,
                },
            });
        });

        view.ui.components = [];
        setWebMap(webmap);
        setMapView(view);

        return () => {
            mapOnUnmount?.();
        };
        // The dispatch function won't change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        center?.latitude,
        center?.longitude,
        mapView,
        mapRef,
        isAlternativeBasemap,
        webmapPortalId,
        mapOnChange,
        mapOnUnmount,
    ]);

    return {
        webMap,
        mapView,
        isWebGL2Supported,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    };
};

type UseEsriBaseMapProps = {
    zoom: number;
    mapOnChange: (data: MapOnChangeParams) => void;
    mapOnUnmount?: (() => void) | undefined;
    center?: GeoPoint;
    isAlternativeBasemap?: boolean;
    webmapPortalId?: string;
    shouldResetMapOnInitialLoad?: boolean;
};
export const useEsriBaseMap = ({
    zoom,
    mapOnChange,
    mapOnUnmount,
    center,
    isAlternativeBasemap,
    webmapPortalId,
    shouldResetMapOnInitialLoad,
}: UseEsriBaseMapProps): EsriMapInterface & {
    mapComponent: React.ReactElement;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
} => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapComponent = <Box w="100%" h="100%" ref={mapRef} data-testid="MAP_COMPONENT" />;
    const {
        mapView,
        webMap,
        isWebGL2Supported,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    } = useEsriESModuleMap({
        zoom,
        mapRef,
        mapOnChange,
        mapOnUnmount,
        center,
        isAlternativeBasemap,
        webmapPortalId,
        shouldResetMapOnInitialLoad,
    });

    return {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        isWebGL2Supported,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    };
};
